import React, { useMemo } from "react";
import {
  TextField,
  FormControl,
  Autocomplete,
  Box,
  createFilterOptions,
  FilterOptionsState,
} from "@mui/material";
import { Controller, Control, FieldError } from "react-hook-form";
import MoneyInput from "@/modules/common/components/input/money-input";
import { FormFactoryDateRules } from "@/modules/common/types";
import { FieldsResponse } from "@/modules/common/dtos/index.v2";
import DatePickerRenderer from "@/modules/lead-page/components/lead-info/edit-fields/status/forms/form-factory/date-picker-renderer";
import TimePickerRenderer from "@/modules/lead-page/components/lead-info/edit-fields/status/forms/form-factory/time-picker-renderer";
import DateTimePickerRenderer from "@/modules/lead-page/components/lead-info/edit-fields/status/forms/form-factory/date-time-picker-renderer";
import BooleanDropdownRenderer from "./boolean-dropdown-renderer";

interface FormFactoryOption {
  value: string;
  label: string;
}

interface FormFieldByTypeProps {
  fieldType: string;
  fullName: string;
  fieldRules: Record<string, any>;
  control: Control;
  status_form_field: FieldsResponse;
  isMulti: boolean;
  options?: FormFactoryOption[];
  setOptions?: (
    value: React.SetStateAction<Record<string, FormFactoryOption>>
  ) => void;
  setValue?: (name: string, value: any) => void;
}

interface GetDropdownValueParams {
  isMulti: boolean;
  value: any;
  fieldOptions: FormFactoryOption[];
  fieldRules: Record<string, any>;
}

const getDropdownValue = ({
  isMulti,
  value,
  fieldOptions,
  fieldRules,
}: GetDropdownValueParams) => {
  if (isMulti) {
    if (Array.isArray(value)) {
      return value.map(
        (v) =>
          fieldOptions.find(
            (option) => option.value === v.value?.toString()
          ) || {
            value: v.value?.toString() || "",
            label: v.label || "",
          }
      );
    }

    if (value) {
      return [
        {
          value: value.value?.toString() || "",
          label: value.label || "",
        },
      ];
    }

    if (fieldRules?.defaultValue) {
      return [
        fieldOptions.find(
          (option) => option.value === fieldRules.defaultValue?.toString()
        ) || {
          value: fieldRules.defaultValue?.toString() || "",
          label: fieldRules.label || "",
        },
      ];
    }

    return [];
  }

  if (value) {
    return (
      fieldOptions.find(
        (option) => option.value === value.value?.toString()
      ) || {
        value: value.value?.toString(),
        label: value.label,
      }
    );
  }

  if (fieldRules?.defaultValue) {
    return (
      fieldOptions.find(
        (option) => option.value === fieldRules.defaultValue?.toString()
      ) || {
        value: fieldRules.defaultValue?.toString(),
        label: fieldRules.label || "",
      }
    );
  }

  return null;
};

const baseFilterOptions = createFilterOptions<FormFactoryOption>({
  trim: true,
});

// Exact match > starts-with > word-start > substring, ties keep original order.
const rankOption = (label: string, input: string): number => {
  if (label === input) return 0;
  if (label.startsWith(input)) return 1;
  if (label.includes(` ${input}`)) return 2;
  return 3;
};

// Only substring matches of the typed text are shown, best match first — the
// dropdown never surfaces options unrelated to what the user typed.
const filterAndRankOptions = (
  options: FormFactoryOption[],
  state: FilterOptionsState<FormFactoryOption>
): FormFactoryOption[] => {
  const input = state.inputValue.trim().toLowerCase();
  if (!input) return options;

  return baseFilterOptions(options, state)
    .map((option, index) => ({
      option,
      index,
      rank: rankOption((option.label || "").toLowerCase(), input),
    }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((entry) => entry.option);
};

interface FormFactoryAutocompleteProps {
  fieldOptions: FormFactoryOption[];
  isMulti: boolean;
  label: string;
  error?: FieldError;
  value: any;
  fieldRules: Record<string, any>;
  onChange: (value: any) => void;
}

const FormFactoryAutocomplete = ({
  fieldOptions,
  isMulti,
  label,
  error,
  value,
  fieldRules,
  onChange,
}: FormFactoryAutocompleteProps) => {
  // Stable identity: getDropdownValue builds fresh objects, and a fresh `value`
  // prop every render makes MUI's reset effect wipe the typed text mid-search.
  const dropdownValue = useMemo(
    () => getDropdownValue({ isMulti, value, fieldOptions, fieldRules }),
    // fieldRules is rebuilt by the parent every render — depend on the fields read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isMulti, value, fieldOptions, fieldRules?.defaultValue, fieldRules?.label]
  );

  return (
    <Autocomplete
      fullWidth
      multiple={isMulti}
      options={fieldOptions}
      autoHighlight
      filterOptions={filterAndRankOptions}
      isOptionEqualToValue={(option, selected) =>
        String(option?.value) === String(selected?.value)
      }
      getOptionLabel={(option: FormFactoryOption) => option?.label || ""}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={!!error}
          helperText={error?.message || ""}
        />
      )}
      onChange={(_, newValue) => {
        onChange(
          isMulti
            ? (newValue as FormFactoryOption[])?.map((item) => item)
            : newValue
            ? (newValue as FormFactoryOption)
            : null
        );
      }}
      value={dropdownValue}
    />
  );
};

const FormFieldByType: React.FC<FormFieldByTypeProps> = ({
  fieldType,
  fullName,
  control,
  status_form_field,
  options,
  isMulti,
  fieldRules,
}) => {
  const fieldWrapper = (children: React.ReactNode) => (
    <Box sx={{ width: "100%" }}>{children}</Box>
  );
  const commonProps = {
    fullWidth: true,
  };

  // Stable options identity — rebuilding this array every render resets MUI
  // Autocomplete internals (typed text, highlighted option) while searching.
  const dropdownOptions = useMemo(
    () =>
      options?.map((option) => ({
        ...option,
        fieldUId: status_form_field.uid,
      })) || [],
    [options, status_form_field.uid]
  );

  const renderIntegerField = () =>
    fieldWrapper(
      !status_form_field.isHidden ? (
        <Controller
          name={fullName}
          control={control}
          rules={{
            required: fieldRules.required ? "This field is required" : false,

            validate: (value) => {
              if (value) {
                if (fieldRules.min && value < fieldRules.min) {
                  return `Value must be greater than or equal to ${fieldRules.min}`;
                }

                if (fieldRules.max && value > fieldRules.max) {
                  return `Value must be less than or equal to ${fieldRules.max}`;
                }

                if (
                  fieldRules.min &&
                  fieldRules.max &&
                  (value < fieldRules.min || value > fieldRules.max)
                ) {
                  return `Value must be between ${fieldRules.min} and ${fieldRules.max}`;
                }
              }

              return true;
            },
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <TextField
              {...commonProps}
              fullWidth
              label={status_form_field.field.label}
              type="number"
              InputProps={{
                inputProps: {
                  min: fieldRules?.min,
                  max: fieldRules?.max,
                },
              }}
              value={value}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const inputValue = parseInt(event.target.value, 10) || null;
                onChange(inputValue);
              }}
              error={!!error}
              helperText={error?.message || ""}
            />
          )}
        />
      ) : null
    );

  const renderPriceField = () =>
    fieldWrapper(
      !status_form_field.isHidden ? (
        <Controller
          name={fullName}
          control={control}
          rules={{
            required: fieldRules.required ? "This field is required" : false,

            min: {
              value: fieldRules.min || fieldRules.defaultValue,
              message: `min value: ${fieldRules?.min}`,
            },
            max: {
              value: fieldRules.max,
              message: `max value: ${fieldRules?.max}`,
            },
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <MoneyInput
              isError={!!error}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const inputValue = parseFloat(event.target.value) || null;

                onChange(inputValue);
              }}
              defaultValue={value || fieldRules.defaultValue || ""}
              label={status_form_field.field.label || ""}
              helperText={error?.message || ""}
            />
          )}
        />
      ) : null
    );

  const renderAutocompleteField = (fieldOptions: FormFactoryOption[]) =>
    fieldWrapper(
      <Controller
        name={fullName}
        control={control}
        rules={{
          required: fieldRules.required ? "This field is required" : false,
        }}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <FormControl fullWidth error={!!error}>
            <FormFactoryAutocomplete
              fieldOptions={fieldOptions}
              isMulti={isMulti}
              label={status_form_field.field.label || ""}
              error={error}
              value={value}
              fieldRules={fieldRules}
              onChange={onChange}
            />
          </FormControl>
        )}
      />
    );

  switch (fieldType) {
    case "integer":
      return renderIntegerField();
    case "price":
      return renderPriceField();
    case "string":
      return fieldWrapper(
        !status_form_field.isHidden ? (
          <Controller
            name={fullName}
            control={control}
            rules={{
              required: fieldRules.required ? "This field is required" : false,
              validate: fieldRules.phone
                ? (value) => {
                    const valueToValidate =
                      typeof value === "string" ? value : value?.value;
                    return (
                      /^\+?[0-9]{1,15}$/.test(valueToValidate) ||
                      "Invalid phone number format - must use country code"
                    );
                  }
                : fieldRules.email
                ? (value) => {
                    const valueToValidate =
                      typeof value === "string" ? value : value?.value;
                    return (
                      /^[^\s@]{1,64}@[^\s@]{1,255}\.[^\s@]{1,63}$/.test(valueToValidate) ||
                      "Invalid email format"
                    );
                  }
                : undefined,
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              return (
                <TextField
                  {...commonProps}
                  fullWidth
                  label={status_form_field.field.label || ""}
                  type="text"
                  value={value}
                  onChange={(e) => {
                    const inputValue = e.target.value;

                    onChange(inputValue);
                  }}
                  error={!!error}
                  helperText={error?.message || " "}
                />
              );
            }}
          />
        ) : null
      );
    case "date":
      return fieldWrapper(
        !status_form_field.isHidden ? (
          <DatePickerRenderer
            fullName={fullName}
            control={control}
            fieldRules={fieldRules as FormFactoryDateRules}
            commonProps={commonProps}
            status_form_field={status_form_field}
          />
        ) : null
      );
    case "time":
      return fieldWrapper(
        !status_form_field.isHidden ? (
          <TimePickerRenderer
            fullName={fullName}
            control={control}
            fieldRules={fieldRules}
            status_form_field={status_form_field}
            commonProps={commonProps}
          />
        ) : null
      );
    case "datetime":
      return fieldWrapper(
        !status_form_field.isHidden ? (
          <DateTimePickerRenderer
            fullName={fullName}
            control={control}
            fieldRules={fieldRules as FormFactoryDateRules}
            commonProps={commonProps}
            status_form_field={status_form_field}
          />
        ) : null
      );
    case "dropdown":
      return fieldWrapper(
        !status_form_field.isHidden
          ? renderAutocompleteField(dropdownOptions)
          : null
      );
    // case "prefilled":
    //   return fieldWrapper(
    //     !status_form_field.isHidden
    //       ? renderAutocompleteSearchField(dynamicList?.dynamicListData || [])
    //       : null
    //   );
    case "dynamic":
      return fieldWrapper(
        !status_form_field.isHidden
          ? renderAutocompleteField(dropdownOptions)
          : null
      );
    case "boolean":
      return fieldWrapper(
        !status_form_field.isHidden ? (
          <BooleanDropdownRenderer formFields={[status_form_field]} />
        ) : null
      );

    default:
      return <div>No Such Type</div>;
  }
};

export default FormFieldByType;
